import type { AxiosInstance } from 'axios'
import { transformPropertyKeys } from '@/api/transforms/property-keys'
import { PropertyKeyModel } from '@/models/property-keys'
import type { PropertyKey, ScheduledPropertyKey } from '../types/property-keys'
import { getAuthTokenFromStorage } from '@/services/utils'


class PropertyKeysService {
    private client: AxiosInstance

    constructor(client: AxiosInstance) {
        this.client = client
    }

    private static get token(): string {
        return getAuthTokenFromStorage()
    }

    async getReservationPropertyKeys(reservationId: string): Promise<PropertyKeyModel[]> {
        const response = await this.client.get<PropertyKey[]>('/property-keys', {
            params: {
                reservationId,
            },
            headers: {
                'Authorization': `Bearer ${ PropertyKeysService.token }`,
            },
        })

        return transformPropertyKeys(response.data.data)
    }

    async getScheduledKeys(reservationId: string): Promise<Date | null> {
        const data = await this.client.post<ScheduledPropertyKey[]>('/actions', {
          $method: 'find',

          $query: {
            reservationId,
            type: 'property-keys',
            key: 'create-property-key',
            processedAt: null
          }
        }, {
          headers: {
            'Authorization': `Bearer ${ PropertyKeysService.token }`,
          }
        });

        const expectedProcessDate = data.data.data[0]?.expectedProcessDate;

        if (typeof expectedProcessDate === 'string') {
          return new Date(expectedProcessDate);
        }

        return null;
    }

    async createReservationPropertyKeys(reservationId: string): Promise<void> {
        await this.client.post<PropertyKey[]>('/actions', {
          'status': 'accepted',
          'data': {
            'payload': {},
            'method': 'patch',
          },
          reservationId,
          'customerId': null,
          'type': 'property-keys',
          'key': 'create-property-key',
          'expectedProcessDate': null,
        }, {
          headers: {
            'Authorization': `Bearer ${ PropertyKeysService.token }`,
          }
        })
    }

    async removePropertyKeys(propertyKeyIds: string[]): Promise<void> {
        const keysToRemove = propertyKeyIds.map(keyId => ({
            'id': keyId,
            '$method': 'remove'
        }))
        await this.client.post('/property-keys', keysToRemove, {
            headers: {
                'Authorization': `Bearer ${ PropertyKeysService.token }`,
            }
        })
    }
}

export { PropertyKeysService }
